"use client";

import { useCurrency } from "@/contexts/CurrencyContext";
import { cn } from "@/lib/utils";
import type { SupportedCurrency } from "@/lib/utils/currency";
import { formatPriceSimple, formatPriceWithCode } from "@/lib/utils/currency";

interface PriceDisplayProps {
  /** 상품의 원본 가격 (DB에 저장된 값) */
  price?: number;
  /** @deprecated price 사용 권장 */
  priceKRW?: number;
  /** 상품의 기준 통화 (판매자가 설정한 통화) */
  sourceCurrency?: SupportedCurrency;
  /** 서버에서 변환된 가격 (깜빡임 방지) */
  serverPrice?: number;
  /** 서버에서 변환된 통화 */
  serverCurrency?: SupportedCurrency;
  /** 크기 */
  size?: "sm" | "md" | "lg";
  /** 추가 CSS 클래스 */
  className?: string;
  /** 인라인 표시 */
  inline?: boolean;
  /** 통화 코드로 표시 (USD 3.14 형태) — 기본: 기호($3.14) */
  showCurrencyCode?: boolean;
}

/**
 * 다중 통화 가격 표시 컴포넌트
 *
 * 상품의 기준 통화에서 사용자가 선택한 통화로 자동 변환하여 표시합니다.
 *
 * @example
 * // 상품별 통화 사용 (권장)
 * <PriceDisplay price={1000} sourceCurrency="JPY" />
 * // 출력: ₩8,630 - KRW 선택 시 (1 JPY = 8.63 KRW)
 * // 출력: ¥1,000 - JPY 선택 시 (동일 통화)
 *
 * @example
 * // 레거시 (USD 기준)
 * <PriceDisplay priceKRW={25} />
 * // 출력: ₩36,053 - KRW 선택 시
 */
export function PriceDisplay({
  price,
  priceKRW, // 레거시 호환성
  sourceCurrency = "USD",
  serverPrice,
  serverCurrency,
  size = "md",
  className,
  inline = true,
  showCurrencyCode = false,
}: PriceDisplayProps) {
  const {
    formatFromSource,
    formatWithCodeFromSource,
    selectedCurrency,
  } = useCurrency();

  // 하위 호환성: priceKRW가 있으면 사용, 없으면 price 사용
  const actualPrice = price ?? priceKRW ?? 0;

  // 서버 변환 가격 우선 사용 (사용자가 통화를 수동 전환하지 않은 경우)
  let formattedPrice: string;
  if (showCurrencyCode) {
    // 통화 코드 형태: "USD 3.14".
    // 반올림은 formatFromSource(기호 표기)와 반드시 같은 정책(ceil)이어야 한다 —
    // 예전엔 여기만 Math.round 라 카드가 상세/결제보다 1단위 싸게 표시됐다.
    if (serverPrice !== undefined && serverCurrency && serverCurrency === selectedCurrency) {
      formattedPrice = formatPriceWithCode(serverPrice, serverCurrency);
    } else {
      formattedPrice = formatWithCodeFromSource(actualPrice, sourceCurrency);
    }
  } else {
    // 기본: 기호 형태 "$3.14"
    formattedPrice =
      serverPrice !== undefined && serverCurrency && serverCurrency === selectedCurrency
        ? formatPriceSimple(serverPrice, serverCurrency)
        : formatFromSource(actualPrice, sourceCurrency);
  }

  // 크기별 스타일
  const sizeStyles = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg font-semibold",
  };

  if (inline) {
    return (
      <span className={cn(sizeStyles[size], className)} suppressHydrationWarning>
        {formattedPrice}
      </span>
    );
  }

  // 블록 레이아웃
  return (
    <div className={cn("flex flex-col", className)}>
      <span className={cn(sizeStyles[size], "font-medium")} suppressHydrationWarning>{formattedPrice}</span>
    </div>
  );
}

/**
 * 일별 가격 표시 (예: ₩50,000/일)
 */
interface PricePerDayDisplayProps extends Omit<PriceDisplayProps, "inline"> {
  /** 일 수 표시 라벨 */
  perDayLabel?: string;
}

export function PricePerDayDisplay({
  price,
  priceKRW, // 레거시 호환성
  sourceCurrency = "USD",
  serverPrice,
  serverCurrency,
  perDayLabel = "/일",
  size = "md",
  className,
}: PricePerDayDisplayProps) {
  const { formatFromSource, selectedCurrency } = useCurrency();

  const actualPrice = price ?? priceKRW ?? 0;
  const formattedPrice =
    serverPrice !== undefined && serverCurrency && serverCurrency === selectedCurrency
      ? formatPriceSimple(serverPrice, serverCurrency)
      : formatFromSource(actualPrice, sourceCurrency);

  const sizeStyles = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg font-semibold",
  };

  return (
    <span className={cn(sizeStyles[size], className)} suppressHydrationWarning>
      {formattedPrice}
      <span className="text-muted-foreground text-sm">{perDayLabel}</span>
    </span>
  );
}

/**
 * 가격 범위 표시 (예: ₩30,000 ~ ₩50,000)
 */
interface PriceRangeDisplayProps {
  /** 최소 가격 */
  minPrice?: number;
  /** 최대 가격 */
  maxPrice?: number;
  /** @deprecated minPrice 사용 권장 */
  minPriceKRW?: number;
  /** @deprecated maxPrice 사용 권장 */
  maxPriceKRW?: number;
  /** 상품의 기준 통화 */
  sourceCurrency?: SupportedCurrency;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function PriceRangeDisplay({
  minPrice,
  maxPrice,
  minPriceKRW, // 레거시 호환성
  maxPriceKRW, // 레거시 호환성
  sourceCurrency = "USD",
  size = "md",
  className,
}: PriceRangeDisplayProps) {
  const { formatFromSource } = useCurrency();

  const actualMinPrice = minPrice ?? minPriceKRW ?? 0;
  const actualMaxPrice = maxPrice ?? maxPriceKRW ?? 0;

  const sizeStyles = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg font-semibold",
  };

  if (actualMinPrice === actualMaxPrice) {
    return (
      <span className={cn(sizeStyles[size], className)} suppressHydrationWarning>
        {formatFromSource(actualMinPrice, sourceCurrency)}
      </span>
    );
  }

  return (
    <span className={cn(sizeStyles[size], className)} suppressHydrationWarning>
      {formatFromSource(actualMinPrice, sourceCurrency)} ~ {formatFromSource(actualMaxPrice, sourceCurrency)}
    </span>
  );
}

export default PriceDisplay;
